import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2, ArrowRight, Sparkles, Zap, BadgeCheck, Wallet, ShieldCheck,
  Loader2, Target, Network, ArrowUpRight, Lock, MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

const API = `${BACKEND_URL}/api`;
const PUBLISHER_REVENUE_SHARE = 0.90;

/**
 * Publisher (lead seller) apply — premium two-column layout.
 *
 * Left column: marketing — earnings calculator, sector grid w/ live CPL,
 *              compliance & trust signals.
 * Right column: sticky multi-step apply form with validation + clean UX.
 */
export default function PublisherApply() {
  const [sectors, setSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(true);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "", website: "",
    country: "GB", sectors: [], monthly_volume_estimate: 1000,
    lead_source: "", consent_capture_method: "", notes: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {application_id}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/catalog/sectors");
        setSectors(data || []);
      } catch (_) {
        setSectors([]);
      } finally {
        setLoadingSectors(false);
      }
    })();
  }, []);

  // ---------- Estimate ----------
  const selectedSectors = useMemo(
    () => sectors.filter((s) => form.sectors.includes(s.slug)),
    [sectors, form.sectors],
  );
  const avgCpl = useMemo(() => {
    if (selectedSectors.length === 0) return 0;
    const sum = selectedSectors.reduce((acc, s) => acc + (Number(s.base_cpl) || 0), 0);
    return Math.round(sum / selectedSectors.length);
  }, [selectedSectors]);
  const monthlyVolume = Math.max(0, form.monthly_volume_estimate || 0);
  const grossPerMonth = Math.round(monthlyVolume * avgCpl);
  const publisherPerMonth = Math.round(grossPerMonth * PUBLISHER_REVENUE_SHARE);
  const annualisedEarnings = publisherPerMonth * 12;

  const toggleSector = (slug) =>
    setForm((f) => ({
      ...f,
      sectors: f.sectors.includes(slug) ? f.sectors.filter((x) => x !== slug) : [...f.sectors, slug],
    }));

  // ---------- Validation ----------
  const validateStep1 = () => {
    const e = {};
    if (form.sectors.length === 0) e.sectors = "Pick at least one sector";
    if (!monthlyVolume || monthlyVolume < 50) e.monthly_volume_estimate = "Estimate at least 50 leads/mo";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep2 = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (!form.lead_source.trim()) e.lead_source = "Required — paid search / SEO / inbound forms / etc.";
    if (!form.consent_capture_method.trim()) e.consent_capture_method = "Required for FCA compliance";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validateStep2()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/publisher/apply`, form);
      setDone({ application_id: data.application_id });
      // Land the user at the top of the success card — never make them
      // hunt for confirmation after submission.
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Done state ----------
  if (done) {
    return (
      <div className="min-h-[80vh] grid place-items-center px-4 py-20">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-soft p-10 text-center" data-testid="publisher-apply-done">
          <div className="mx-auto inline-grid place-items-center size-14 rounded-2xl bg-[#0F172A] text-white">
            <CheckCircle2 className="size-6" strokeWidth={1.7} />
          </div>
          <h1 className="mt-6 text-[28px] font-semibold text-slate-900 tracking-[-0.02em]">Application received.</h1>
          <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
            Our partner ops team will review and respond within 24 hours.
            Approved publishers receive an API key + endpoint by email and full dashboard access.
          </p>
          <p className="mt-5 text-[11px] font-mono text-slate-400">Ref · {done.application_id.slice(0, 8)}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-2 sm:justify-center">
            <Link to="/"><Button variant="outline" className="rounded-full">Back to home</Button></Link>
            <Link to="/login"><Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]">Sign in <ArrowRight className="size-4" /></Button></Link>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Page ----------
  return (
    <div className="bg-white" data-testid="publisher-apply-page">
      {/* ======================== HERO ======================== */}
      <section className="relative border-b border-slate-200 overflow-hidden bg-slate-50/40">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 size-[480px] rounded-full bg-[#0055FF]/[0.05] blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-16 lg:pt-24 lg:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 pl-2 pr-3 py-1 text-[11px] font-medium text-slate-700 shadow-soft">
              <span className="inline-block size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="uppercase tracking-[0.16em] text-[10px] text-slate-500">Publishers · accepting applications</span>
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-[-0.045em] font-semibold text-slate-900 leading-[0.95]">
              Sell your UK<br />finance leads — <span className="text-[#0055FF]">paid per accepted lead.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-[17px] sm:text-[18px] text-slate-600 leading-relaxed">
              Push leads into Connexis via API. Our routing engine matches each lead to its single exclusive buyer in sub-second at the fixed sector CPL. You earn <b className="text-slate-900">90% of every accepted lead</b>, withdrawable weekly via Stripe Connect or bank transfer.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl">
            <HeroPill Icon={Zap} title="Real-time routing" body="Sub-second buyer match via Ping Tree v2." />
            <HeroPill Icon={Wallet} title="90% revenue share" body="Industry-best split. Pay-out weekly." />
            <HeroPill Icon={BadgeCheck} title="FCA-aware" body="We enforce consent + suppression." />
            <HeroPill Icon={Sparkles} title="No minimums" body="Push 10 or 10,000/day. No floors." />
          </div>
        </div>
      </section>

      {/* ======================== MAIN 2-COLUMN ======================== */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16">
          {/* ============ LEFT: Sectors + Calculator + Trust ============ */}
          <div className="space-y-14">
            {/* Sectors we route */}
            <div>
              <SectionLabel>01 · Sectors we route</SectionLabel>
              <h2 className="mt-3 text-3xl sm:text-[40px] tracking-[-0.025em] font-semibold text-slate-900 leading-tight">10 finance verticals with active buyer demand.</h2>
              <p className="mt-3 text-[15px] text-slate-600 max-w-xl leading-relaxed">Live base CPL is what buyers pay us. You earn 90% on every accepted lead.</p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {loadingSectors ? (
                  Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[88px] rounded-xl bg-slate-100 animate-pulse" />)
                ) : sectors.length === 0 ? (
                  <div className="sm:col-span-2 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-5 text-[13px] text-amber-900" data-testid="sectors-load-error">
                    <div className="font-semibold mb-1">Sectors didn't load.</div>
                    <div className="text-amber-800/80">This is usually a transient network hiccup. Tap retry — or just refresh the page.</div>
                    <button
                      type="button"
                      onClick={async () => {
                        setLoadingSectors(true);
                        try {
                          const { data } = await api.get("/catalog/sectors");
                          setSectors(data || []);
                        } catch (_) { setSectors([]); }
                        finally { setLoadingSectors(false); }
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-900 text-white text-[12px] font-medium px-3 py-1.5 hover:bg-amber-950 transition-colors"
                      data-testid="sectors-retry-btn"
                    >Retry</button>
                  </div>
                ) : sectors.map((s) => (
                  <SectorCard key={s.slug} sector={s} selected={form.sectors.includes(s.slug)} onClick={() => toggleSector(s.slug)} />
                ))}
              </div>
              {errors.sectors && <p className="mt-3 text-[12px] text-rose-600">{errors.sectors}</p>}
            </div>

            {/* Earnings calculator */}
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white p-7 lg:p-9 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 size-72 rounded-full bg-[#0055FF]/20 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#3D7BFF]">02 · Live earnings preview</div>
                <h3 className="mt-2 text-2xl tracking-[-0.02em] font-semibold">What you'd earn on Connexis</h3>
                <p className="mt-1.5 text-[13px] text-slate-300">Updates as you pick sectors + set monthly volume on the right.</p>

                <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-6">
                  <Metric label="Avg buyer CPL" value={avgCpl > 0 ? `£${avgCpl}` : "—"} sub="across selected sectors" />
                  <Metric label="Your share / lead" value={avgCpl > 0 ? `£${Math.round(avgCpl * PUBLISHER_REVENUE_SHARE)}` : "—"} sub="90% revenue share" />
                  <Metric label="Per month" value={`£${publisherPerMonth.toLocaleString()}`} sub={`at ${monthlyVolume.toLocaleString()} leads/mo`} accent />
                  <Metric label="Annualised" value={`£${annualisedEarnings.toLocaleString()}`} sub="based on current volume" />
                </div>

                <div className="mt-7 pt-6 border-t border-white/10 grid grid-cols-3 gap-3 text-[11.5px]">
                  <MicroStat Icon={Target} label="Accept rate" value=">82%" />
                  <MicroStat Icon={Zap} label="Pay cadence" value="Weekly" />
                  <MicroStat Icon={ShieldCheck} label="Buyer net" value="100% vetted" />
                </div>
              </div>
            </div>

            {/* Compliance & trust */}
            <div className="grid sm:grid-cols-2 gap-4">
              <TrustCard Icon={Lock} title="Consent gated" body="TrustedForm / Jornaya / form-only — we enforce it before any buyer touches a lead." />
              <TrustCard Icon={Network} title="No buyer race" body="Ping tree is auctioned at fixed rules — your premium leads always win the top price." />
              <TrustCard Icon={MessagesSquare} title="Real-time feedback" body="Reject / accept reasons stream back so you can tune your sources." />
              <TrustCard Icon={ArrowUpRight} title="Direct API" body="POST one JSON payload per lead. Live in 15 mins." />
            </div>
          </div>

          {/* RIGHT: Sticky multi-step form */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div id="apply-form-anchor" className="rounded-2xl border border-slate-200 bg-white shadow-float overflow-hidden scroll-mt-24" data-testid="publisher-apply-form">
              <div className="px-7 pt-7 pb-5 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <SectionLabel>03 · Apply to publish</SectionLabel>
                  <div className="text-[11px] font-mono text-slate-400">Step {step}/2</div>
                </div>
                <h3 className="mt-2 text-2xl tracking-[-0.02em] font-semibold text-slate-900">{step === 1 ? "Volume & sectors" : "Your operation"}</h3>
                <p className="mt-1 text-[13px] text-slate-600">{step === 1 ? "Tell us what you can produce." : "Tell us who's behind the leads."}</p>
                <div className="mt-5 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0055FF] transition-all duration-500" style={{ width: step === 1 ? "50%" : "100%" }} />
                </div>
              </div>

              <div className="p-7">
                {step === 1 ? (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-[12px] font-medium text-slate-700 mb-2 block">Selected sectors <span className="text-slate-400 font-normal">· tap on the left to choose</span></Label>
                      <div className="min-h-[44px] rounded-xl border border-dashed border-slate-200 p-2.5 flex flex-wrap gap-1.5">
                        {form.sectors.length === 0 ? (
                          <span className="text-[12.5px] text-slate-400 italic px-2 py-1">None yet — pick from the sector tiles ←</span>
                        ) : selectedSectors.map((s) => (
                          <button key={s.slug} type="button" onClick={() => toggleSector(s.slug)} className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] text-white text-[12px] font-medium pl-2.5 pr-1.5 py-1 hover:bg-[#0044CC] transition-colors" data-testid={`chip-${s.slug}`}>
                            {s.name}<span className="size-4 grid place-items-center rounded-full bg-white/20 text-[10px] leading-none">×</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Field label="Estimated monthly volume" hint="How many leads can you produce per month across the sectors above?" error={errors.monthly_volume_estimate}>
                      <div className="grid grid-cols-2 gap-3 items-center">
                        <Input type="number" min={50} max={10_000_000} value={form.monthly_volume_estimate} onChange={(e) => setForm({ ...form, monthly_volume_estimate: parseInt(e.target.value || "0", 10) })} className="h-11 text-base font-medium tabular-nums" data-testid="pub-volume" />
                        <div className="flex gap-1.5 flex-wrap">
                          {[500, 2500, 10000].map((v) => (
                            <button key={v} type="button" onClick={() => setForm({ ...form, monthly_volume_estimate: v })} className={`text-[11.5px] rounded-full px-2.5 py-1 border transition-colors ${form.monthly_volume_estimate === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                              {v.toLocaleString()}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* iter90u — soft screening: <100 leads/mo nudges to Affiliate Programme instead */}
                      {monthlyVolume > 0 && monthlyVolume < 100 && (
                        <div className="mt-3 rounded-lg border border-[#0055FF]/30 bg-[#0055FF]/[0.04] px-3.5 py-3 flex items-start gap-2.5" data-testid="pub-volume-affiliate-nudge">
                          <Sparkles className="size-3.5 text-[#0055FF] mt-0.5 shrink-0" />
                          <div className="text-[12.5px] text-slate-700 leading-relaxed">
                            <span className="font-medium text-slate-900">Connexis Publisher accounts are best-suited to sources delivering 100+ leads/month.</span>{" "}
                            At your current volume you may earn more, faster, through our{" "}
                            <Link to="/login?tab=signup" className="text-[#0055FF] font-medium hover:underline">Affiliate Programme</Link>{" "}
                            — share your tracking link and earn lifetime commission per signup, no application required. You can still continue this application if you prefer.
                          </div>
                        </div>
                      )}
                    </Field>

                    <Field label="Country" hint="Where is your business based?">
                      <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-[14px] text-slate-900 focus:outline-none focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]/30" data-testid="pub-country">
                        <option value="GB">United Kingdom</option>
                        <option value="IE">Ireland</option>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </Field>

                    <Button onClick={() => { if (validateStep1()) { setStep(2); requestAnimationFrame(() => document.getElementById("apply-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })); } }} className="w-full h-12 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-[14px] font-semibold" data-testid="pub-next">
                      Continue · tell us about you <ArrowRight className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Company" error={errors.company}>
                        <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Lead Co" className="h-11" data-testid="pub-company" />
                      </Field>
                      <Field label="Your name" error={errors.contact_name}>
                        <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Alex Smith" className="h-11" data-testid="pub-name" />
                      </Field>
                    </div>
                    <Field label="Work email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="alex@acme.io" className="h-11" data-testid="pub-email" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Phone" hint="Optional">
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 7700 900000" className="h-11" data-testid="pub-phone" />
                      </Field>
                      <Field label="Website" hint="Optional">
                        <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://acme.io" className="h-11" data-testid="pub-website" />
                      </Field>
                    </div>
                    <Field label="Primary lead source" hint="Paid search, SEO, social, inbound forms, partnerships…" error={errors.lead_source}>
                      <Input value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} placeholder="Paid search + SEO" className="h-11" data-testid="pub-source" />
                    </Field>
                    <Field label="Consent capture method" hint="TrustedForm, Jornaya, form-only, etc. — required for FCA compliance" error={errors.consent_capture_method}>
                      <Input value={form.consent_capture_method} onChange={(e) => setForm({ ...form, consent_capture_method: e.target.value })} placeholder="TrustedForm" className="h-11" data-testid="pub-consent" />
                    </Field>
                    <Field label="Anything else?" hint="Optional · tell us about your stack, scale, or compliance approach">
                      <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="We capture mortgage leads via paid search with TrustedForm consent + manual phone validation. Currently routing to Acme Mortgages Ltd, looking to scale." data-testid="pub-notes" />
                    </Field>

                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" onClick={() => { setStep(1); requestAnimationFrame(() => document.getElementById("apply-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })); }} className="rounded-full h-11 px-5" data-testid="pub-back">Back</Button>
                      <Button onClick={submit} disabled={busy} className="flex-1 h-12 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-[14px] font-semibold" data-testid="pub-submit">
                        {busy ? <><Loader2 className="size-4 animate-spin" /> Submitting…</> : <>Submit application <ArrowRight className="size-4" /></>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-7 py-4 bg-slate-50/60 border-t border-slate-100 flex items-center gap-2 text-[11.5px] text-slate-500">
                <ShieldCheck className="size-3.5" /> No commitment · approval typically &lt;24h · already a publisher? <Link to="/login" className="text-[#0055FF] hover:underline">Sign in</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================== HOW IT WORKS STRIP ======================== */}
      <section className="border-b border-slate-200 bg-slate-50/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
          <SectionLabel>04 · How it works</SectionLabel>
          <h2 className="mt-3 text-3xl sm:text-[40px] tracking-[-0.025em] font-semibold text-slate-900 max-w-2xl leading-tight">From application to first payout, in 4 steps.</h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: "01", title: "Apply", body: "Submit volume + sectors + compliance info. We review every application personally — most are approved same-day." },
              { n: "02", title: "Get your API key", body: "Receive your `pk_live_*` key and our REST endpoint by email. Live integration in 15 mins via curl, Postman or your CRM." },
              { n: "03", title: "Push leads", body: "POST JSON to `/api/v1/leads/ingest`. We validate consent, dedup, and route to the top-bidding buyer in our ping tree." },
              { n: "04", title: "Get paid", body: "90% per accepted lead credited to your ledger. Withdraw weekly via Stripe Connect or bank transfer. No minimums." },
            ].map((s) => (
              <div key={s.n} className="relative p-7 bg-white border border-slate-200 rounded-xl hover:border-[#0055FF]/30 transition-colors duration-300 group">
                <span className="font-mono text-[10.5px] tracking-[0.2em] text-slate-400">{s.n}</span>
                <h4 className="mt-3 font-semibold text-[17px] text-slate-900 tracking-tight">{s.title}</h4>
                <p className="text-[13.5px] text-slate-600 leading-relaxed mt-2.5">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== FINAL CTA ======================== */}
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <SectionLabel light>Ready when you are</SectionLabel>
          <h2 className="mt-4 text-4xl sm:text-5xl tracking-[-0.03em] font-semibold leading-tight">Turn your lead volume into a real revenue line.</h2>
          <p className="mt-5 text-[15px] text-slate-300 max-w-xl mx-auto leading-relaxed">Apply now and we'll get back to you within 24 hours. Approved publishers can be live in their first integration the same day.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => { setStep(1); setTimeout(() => document.getElementById("apply-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }} className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 hover:bg-slate-100 transition-colors font-semibold text-[14px] h-12 px-6" data-testid="publisher-cta-apply-now">
              Apply now <ArrowRight className="size-4" />
            </button>
            <Link to="/partners" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:bg-white/[0.06] transition-colors text-[14px] h-12 px-6 text-white">
              Looking to refer buyers instead?
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ======================== Sub-components ======================== */

function SectionLabel({ children, light }) {
  return (
    <div className={`inline-flex items-center text-[10.5px] font-semibold tracking-[0.22em] uppercase ${light ? "text-[#3D7BFF]" : "text-[#0055FF]"}`}>
      {children}
    </div>
  );
}

function HeroPill({ Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 hover:border-[#0055FF]/30 hover:shadow-soft transition-all duration-300">
      <Icon className="size-[18px] text-[#0055FF]" strokeWidth={1.6} />
      <div className="mt-2.5 text-[13.5px] font-semibold text-slate-900 tracking-tight">{title}</div>
      <div className="text-[12px] text-slate-600 mt-0.5 leading-relaxed">{body}</div>
    </div>
  );
}

function SectorCard({ sector, selected, onClick }) {
  const earn = Math.round((sector.base_cpl || 0) * PUBLISHER_REVENUE_SHARE);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`sector-card-${sector.slug}`}
      className={`group relative text-left rounded-xl border p-4 transition-all duration-300 ${selected ? "border-[#0055FF] bg-[#0055FF]/[0.04] shadow-soft ring-1 ring-[#0055FF]/15" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-soft"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-[14px] tracking-tight truncate">{sector.name}</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-1">{sector.tagline}</div>
        </div>
        <span className={`shrink-0 size-5 rounded-full grid place-items-center transition-all ${selected ? "bg-[#0055FF] text-white" : "border-2 border-slate-200 bg-white"}`}>
          {selected && <CheckCircle2 className="size-3" strokeWidth={2.5} />}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11.5px]">
        <span className="text-slate-500 font-mono tabular-nums">{sector.volume_band}</span>
        <span className={`tabular-nums font-semibold ${selected ? "text-[#0055FF]" : "text-slate-700"}`}>£{sector.base_cpl}/lead · you keep £{earn}</span>
      </div>
    </button>
  );
}

function Metric({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</div>
      <div className={`mt-1.5 text-[28px] sm:text-[32px] tracking-[-0.02em] font-semibold tabular-nums leading-none ${accent ? "text-[#3D7BFF]" : "text-white"}`}>{value}</div>
      <div className="mt-1 text-[11.5px] text-slate-400">{sub}</div>
    </div>
  );
}

function MicroStat({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-white/[0.04] border border-white/10 px-2.5 py-2">
      <Icon className="size-3.5 text-[#3D7BFF] shrink-0" strokeWidth={1.7} />
      <div className="min-w-0 leading-tight">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
        <div className="text-[13px] text-white font-semibold tabular-nums truncate">{value}</div>
      </div>
    </div>
  );
}

function TrustCard({ Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0055FF]/30 transition-colors duration-300">
      <span className="inline-grid place-items-center size-9 rounded-md bg-[#0F172A] text-white"><Icon className="size-4" strokeWidth={1.6} /></span>
      <div className="mt-3.5 font-semibold text-slate-900 text-[14px] tracking-tight">{title}</div>
      <div className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">{body}</div>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <Label className="text-[12px] font-medium text-slate-700">{label}</Label>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
      {error && <p className="mt-1 text-[11.5px] text-rose-600">{error}</p>}
    </div>
  );
}
